import React, { useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import {
  CleanAccount,
  CleanCustomsDeliveryOrderInvoice,
} from '../../../../../export-types/cleaned-types';
import {
  ModalProps,
  ModalsService,
} from '../../../../../services/modals.service';
import { useFieldArray, useForm } from 'react-hook-form';
import { trpcSwr } from '../../../../../api/trpc';
import { AlertService } from '../../../../../services/alert.service';
import { InputWrapper } from '../../../../common/input-wrapper';
import {
  decimalValidation,
  intValidation,
  required,
} from '../../../../../utils/validations';
import { Select } from '../../../../common/select.component';
import useSWR from 'swr';
import { fetcher } from '../../../../../services/api';
import { CreateUserAccountModal } from '../../../../common/create-user-account-modal';
import { toMoney } from '../../../../../utils/common.utils';

type Props = ModalProps & {
  defaultValues?: CleanCustomsDeliveryOrderInvoice;
  orderId: string;
};

type Form = {
  accountId: number;
  receiverId: number;
  goods: Array<{
    name: string;
    unit: string;
    amount: string;
    price: string;
  }>;
};

export const EditCustomsDeliveryOrderInvoiceModal: React.FC<Props> = (
  props,
) => {
  const { data: order } = trpcSwr.customsDelivery.getOne.useSWR({
    id: props.orderId,
  });
  const { data: accounts, mutate: accountsMutate } = useSWR<{
    list: CleanAccount[];
  }>(() => {
    if (!order?.user?.internalId) {
      return null;
    }
    return {
      url: `/accounts/user/${order?.user?.internalId}`,
    };
  }, fetcher);
  const accountOptions = useMemo(() => {
    return (accounts?.list || []).map((account) => ({
      value: account.id,
      label: account.name,
    }));
  }, [accounts]);
  const { register, handleSubmit, control, watch } = useForm<Form>({
    defaultValues: props.defaultValues?.goods
      ? {
          ...props.defaultValues,
          receiverId: props.defaultValues.receiverAccount?.id,
          goods: (props.defaultValues.goods || []).map((good) => ({
            ...good,
            price: good.price.toString(),
            amount: good.amount.toString(),
          })),
        }
      : { goods: [{ unit: 'шт' }] },
  });
  const { fields, append, remove } = useFieldArray({
    control,
    name: 'goods',
  });
  const { trigger: addInvoice } =
    trpcSwr.customsDeliveryInvoice.addInvoice.useSWRMutation();
  const { trigger: updateInvoice } =
    trpcSwr.customsDeliveryInvoice.updateInvoice.useSWRMutation();
  const { data: receiverAccounts } =
    trpcSwr.customsDeliveryInvoice.getReceiverAccounts.useSWR();
  const receiverAccountOptions = useMemo(() => {
    return (receiverAccounts || []).map((account) => ({
      value: account.id,
      label: account.name,
    }));
  }, [receiverAccounts]);
  const onSubmit = handleSubmit(async (form) => {
    const processedData = {
      accountId: +form.accountId,
      receiverAccountId: +form.receiverId,
      goods: form.goods.map((good) => {
        return { ...good, amount: +good.amount, price: +good.price };
      }),
    };
    if (props.defaultValues) {
      updateInvoice({
        invoiceId: props.defaultValues.uuid,
        data: processedData,
      })
        .then(() => {
          AlertService.success();
          props.close();
        })
        .catch((e) => {
          AlertService.error(e.message);
        });
    } else {
      addInvoice({ orderId: props.orderId, data: processedData })
        .then(() => {
          AlertService.success();
          props.close();
        })
        .catch((e) => {
          AlertService.error(e.message);
        });
    }
  });
  return (
    <div className="modal-dialog">
      <div className={'modal-content'}>
        <div className="modal-header border-bottom-0 p-4">
          <h3>
            <FormattedMessage
              defaultMessage="Создание счёта на оплату"
              id="editCustomsDeliveryOrderInvoiceModal.input.title"
              description="Заголовок модального окна"
            />
          </h3>
          <button
            type="button"
            className="btn-close"
            onClick={() => props.reject()}
          />
        </div>

        <div className="modal-body pt-0 p-4">
          <form>
            <div className={'w-100 position-relative'}>
              <div
                className={
                  'btn btn-ghost text-decoration-underline position-absolute end-0'
                }
                style={{ top: '-5px' }}
                onClick={async () => {
                  if (order?.user) {
                    await ModalsService.createModal(CreateUserAccountModal, {
                      user: order.user,
                    });
                    accountsMutate();
                  }
                }}
              >
                Добавить
              </div>
              <InputWrapper className="mt-3 w-100" title="Плательщик" required>
                <Select
                  className="mb-3"
                  placeholder={''}
                  options={accountOptions}
                  value={watch(`accountId`)}
                  {...register(`accountId`, { ...required })}
                />
              </InputWrapper>
            </div>
            <InputWrapper className="mt-3 w-100" title="Получатель" required>
              <Select
                className="mb-3"
                placeholder={''}
                options={receiverAccountOptions}
                value={watch(`receiverId`)}
                {...register(`receiverId`, { ...required })}
              />
            </InputWrapper>

            {fields.map((_, i) => {
              return (
                <div className={' border-bottom py-3 position-relative'}>
                  {fields.length > 1 ? (
                    <div
                      className={'position-absolute btn btn-ghost top-0 end-0 '}
                      onClick={() => {
                        remove(i);
                      }}
                    >
                      <i className={'bi bi-x fs-20 '}></i>
                    </div>
                  ) : null}

                  <div className={'d-flex gap-2'}>
                    <InputWrapper
                      className="w-100"
                      required
                      title="Наименование товара, работ, услуг"
                    >
                      <input
                        type="text"
                        required
                        placeholder="Наименование товара, работ, услуг"
                        className="form-control"
                        {...register(`goods.${i}.name`, { ...required })}
                      />
                    </InputWrapper>
                  </div>

                  <div className={'d-flex gap-2'}>
                    <InputWrapper
                      className="mt-3 w-50"
                      title="Ед. изм."
                      required
                    >
                      <Select
                        className="mb-3"
                        placeholder={''}
                        options={[
                          { value: 'шт', label: 'Штука (шт)' },
                          { value: 'кг', label: 'Килограмм (кг)' },
                          {
                            value: 'м',
                            label: 'Метр (м)',
                          },
                          { value: 'л', label: 'Литр (л)' },
                          { value: 'ч', label: 'Часы (ч)' },
                          { value: 'т', label: 'Тонна (т)' },

                          {
                            value: 'услуга',
                            label: 'Услуга (услуга)',
                          },
                        ]}
                        value={watch(`goods.${i}.unit`)}
                        {...register(`goods.${i}.unit`, { ...required })}
                      />
                    </InputWrapper>
                    <InputWrapper className="mt-3" title="Кол-во" required>
                      <input
                        type="text"
                        placeholder="Кол-во"
                        required
                        className="form-control"
                        {...register(`goods.${i}.amount`, {
                          ...required,
                          ...intValidation,
                        })}
                      />
                    </InputWrapper>
                  </div>
                  <InputWrapper className="" title="Цена за ед. Р" required>
                    <input
                      type="text"
                      placeholder="Цена за единицу"
                      required
                      className="form-control"
                      {...register(`goods.${i}.price`, {
                        ...required,
                        ...decimalValidation,
                      })}
                    />
                  </InputWrapper>
                </div>
              );
            })}
            <div
              className={'btn btn-ghost mt-2 align-items-center'}
              onClick={() => {
                append({ unit: 'шт', price: '', amount: '', name: '' });
              }}
            >
              <i className={'bi bi-plus fs-18 '}></i>Добавить товар или услугу
            </div>

            <div className={'mleft-auto d-flex align-items-center gap-2'}>
              <div
                className={'btn btn-success mt-2 '}
                onClick={() => {
                  onSubmit();
                }}
              >
                Сохранить
              </div>
              <div className={'mleft-auto fw-600 fs-18'}>
                Сумма:{' '}
                {toMoney(
                  watch('goods').reduce((acc, good) => {
                    return acc + +good.price * +good.amount;
                  }, 0),
                  'rub',
                )}
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};
